import type { ReaderName } from "@bindery/shared/contracts/session";
import { Button } from "@bindery/ui/components/ui/button";
import { Input } from "@bindery/ui/components/ui/input";
import { Label } from "@bindery/ui/components/ui/label";
import { useEffect } from "react";
import { Link, useLocation, useParams } from "react-router";
import { LoadingLines, Prompt, SignedIn } from "../chrome/signed-in";
import { Workbench } from "../chrome/workbench";
import { WorksheetSheet } from "../components/sheet/worksheet-sheet";
import { usePrintName, useWorksheet } from "../data/use-worksheets";
import { formatDate } from "../lib/format-date";
import { readSheetHandoff } from "../lib/sheet-handoff";
import { gradePath, ROUTES } from "../routes";
import { readerDisplayName } from "../lib/reader-name";

const SHEET_LABEL_LENGTH = 6;

function sheetLabelOf(worksheetId: string): string {
  return worksheetId.slice(-SHEET_LABEL_LENGTH);
}

function SheetContent({
  worksheetId,
  reader,
}: {
  worksheetId: string;
  reader: ReaderName | null;
}) {
  const handoff = readSheetHandoff(useLocation().state);
  const worksheet = useWorksheet(worksheetId);
  const [printName, setPrintName] = usePrintName(
    handoff.printName ?? (reader === null ? "" : readerDisplayName(reader)),
  );

  useEffect(() => {
    const previous = document.title;
    document.title =
      printName === ""
        ? "Reading worksheet"
        : `Reading worksheet, ${printName}`;
    return () => {
      document.title = previous;
    };
  }, [printName]);

  if (reader === null) {
    return (
      <Prompt
        title="Choose a reader first"
        action={
          <Button asChild>
            <Link to={ROUTES.home}>Choose a reader</Link>
          </Button>
        }
      >
        <p>A worksheet opens for the reader it was made for.</p>
      </Prompt>
    );
  }
  if (worksheet === undefined) return <LoadingLines />;
  if (worksheet === null) {
    return (
      <Prompt
        title="No such worksheet"
        action={
          <Button variant="outline" asChild>
            <Link to={ROUTES.home}>
              Back to {readerDisplayName(reader)}’s list
            </Link>
          </Button>
        }
      >
        <p>
          It isn’t in {readerDisplayName(reader)}’s worksheets. If it was made
          for another reader, choose that reader first.
        </p>
      </Prompt>
    );
  }

  const gradeHref = gradePath(worksheetId, handoff.gradeToken);
  const gradeUrl = new URL(gradeHref, window.location.origin).href;

  return (
    <div className="flex flex-col gap-6">
      <div className="print-hidden flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-2">
          <Label htmlFor="print-name">Name on the sheet</Label>
          <Input
            id="print-name"
            className="w-56"
            value={printName}
            autoComplete="off"
            onChange={(event) => setPrintName(event.target.value)}
          />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {worksheet.gradedAt === null ? (
            <Button variant="outline" asChild>
              <Link to={gradeHref}>Grade</Link>
            </Button>
          ) : (
            <span className="text-ink-graded text-sm">
              Graded {formatDate(worksheet.gradedAt)}
            </span>
          )}
          <Button onClick={() => window.print()}>Print</Button>
          <Button variant="ghost" asChild>
            <Link to={ROUTES.home}>Back</Link>
          </Button>
        </div>
      </div>
      <WorksheetSheet
        content={worksheet.content}
        printName={printName}
        gradeUrl={gradeUrl}
        qrCarriesToken={handoff.gradeToken !== undefined}
        sheetLabel={sheetLabelOf(worksheetId)}
      />
    </div>
  );
}

export function WorksheetPage() {
  const { worksheetId = "" } = useParams();
  return (
    <Workbench wide>
      <SignedIn>
        {(session) => (
          <SheetContent
            worksheetId={worksheetId}
            reader={session.boundReader}
          />
        )}
      </SignedIn>
    </Workbench>
  );
}
